/* eslint-disable max-lines */
import { getChronicleDeviceFileNameForSession } from '@/atoms/chronicle/session-device-files';
import {
  getCurrentSessionGeneration,
  getOnlyActiveGenerationForTests,
  isCurrentGeneration,
  type GenerationHandle,
} from '@/features/note-generation/generation-coordinator';
import {
  trackNoteGenerationFinished,
  trackNoteGenerationStart,
  type NoteGenerationTimings,
} from '@/features/note-generation/noteGenerationEventTracking';
import { setConsultNoteFromCandidate } from '@/features/sideBySideNoteGen/utils';
import { countMarkdownSections } from '@/helpers/sectionParser';
import { track } from '@/lib/analytics/track';
import { createAllCandidatesFailedError } from '@/lib/telemetry/noteGenerateTracking';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { updateConsultNote } from '@/pages/api/scribeAPIV2';
import { postSetupConsultNote } from '@/pages/api/scribeAPIV2_2';
import {
  getConsultNoteSettingsFromQueryCache,
  setConsultNoteAsFailed,
  setConsultNoteQueryDataFallbackFromSideBySide,
  updateConsultNoteStreamStatus,
} from '@/utils/consultNoteStreamHelpers';
import { createExperimentProps } from '@/utils/experimentNameConverter';
import { logger } from '@/utils/logger/logger';
import {
  getIsPinnedTemplateFromCache,
  getIsSectionalTemplateFromCache,
  getIsStarterTemplateFromCache,
  getTemplateNameFromCache,
} from '@/utils/templateCacheHelpers';
import { sanitizeError } from '@heidi/runtime/logger';
import { ContentType } from '@heidi/schema/ml-scribe';
import { type SideBySideConsultNoteSetupRequest } from '@heidi/session/contracts';
import { invalidateSessionById } from '@heidi/session/data';
import { differenceInMilliseconds } from 'date-fns';
import { atom, type Getter, type Setter } from 'jotai';
import { shuffle } from 'lodash-es';
import {
  transitionSideBySidePhase,
  type SideBySidePhaseEvent,
} from '../generation/side-by-side-phase';
import { streamConsultNoteGenerationV2_2 } from '../streaming';
import {
  SideBySideStreamStatus,
  type SideBySideConsultNoteCandidateWithStream,
  type StreamType,
  type WithSelection,
} from '../types';
import {
  createInitialSideBySideSessionState,
  internalNoteCandidatesAtomFamily,
  noteCandidatesAtomFamily,
  noteDisplayedAnalyticsSentAtomFamily,
  noteDisplayedTimeAtomFamily,
  rawExperimentNameGenerationModeAtomFamily,
  shouldFallbackToSingleNoteViewAtomFamily,
  shouldShowSideBySideNotesAtomFamily,
  sideBySideSessionStateAtomFamily,
  sideBySideSetupLoadingAtomFamily,
} from './atoms';

// Fires first_document_created once per page load (resets on browser reload, not per session).
let hasTrackedFirstDocument = false;

const isAbortError = (error: unknown) =>
  error instanceof Error &&
  (error.name === 'AbortError' || error.name === 'CanceledError');

const transitionSessionPhase = (
  get: Getter,
  set: Setter,
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>,
  event: SideBySidePhaseEvent
) => {
  const stateAtom = sideBySideSessionStateAtomFamily(handle.sessionId);
  const state = get(stateAtom);
  if (state.generationId !== handle.generationId) return;
  set(stateAtom, {
    ...state,
    phase: transitionSideBySidePhase(state.phase, event),
  });
};

export const beginSideBySideGenerationAction = atom(
  null,
  (_get, set, handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>) => {
    const initial = createInitialSideBySideSessionState();
    set(sideBySideSessionStateAtomFamily(handle.sessionId), {
      ...initial,
      generationId: handle.generationId,
      phase: 'waitingForUpload',
      setupLoading: false,
    });
  }
);

function calculateTimingMetrics(
  candidateTimings: SideBySideConsultNoteCandidateWithStream['timings']
) {
  const { firstChunkTime, mostRecentChunkTime, startTime } = candidateTimings;

  const timeToFirstToken =
    firstChunkTime && startTime
      ? differenceInMilliseconds(firstChunkTime, startTime)
      : undefined;

  const timeToLastToken =
    mostRecentChunkTime && startTime
      ? differenceInMilliseconds(mostRecentChunkTime, startTime)
      : undefined;

  return { timeToFirstToken, timeToLastToken };
}

function getStreamedNoteText(
  candidates: SideBySideConsultNoteCandidateWithStream[],
  consultNoteId: string
): string {
  return (
    candidates
      .find((c) => c.consult_note_id === consultNoteId)
      ?.stream.map((s) => s.data)
      .join('') ?? ''
  );
}

function handleStreamMessageForCandidate(
  candidate: SideBySideConsultNoteCandidateWithStream,
  streamData: StreamType,
  handle: GenerationHandle,
  set: Setter
) {
  // An abort can race an already-queued callback. The candidate signal prevents
  // selective-skip resurrection; the generation check prevents superseded work
  // from mutating a newer run for the same session.
  if (candidate.abortController.signal.aborted || !isCurrentGeneration(handle)) return;

  set(updateCandidateStatusAction, {
    ...handle,
    candidateId: candidate.consult_note_id,
    status: SideBySideStreamStatus.STREAMING,
  });
  set(pushCandidateStreamDataAction, {
    ...handle,
    candidateId: candidate.consult_note_id,
    streamData,
  });

  set(
    internalNoteCandidatesAtomFamily(handle.sessionId),
    (draft: SideBySideConsultNoteCandidateWithStream[]) => {
      const toModify = draft.find(
        (candidateInStore) =>
          candidateInStore.consult_note_id === candidate.consult_note_id
      );
      if (!toModify) return;

      toModify.timings.mostRecentChunkTime = new Date();
      if (toModify.timings.firstChunkTime === null) {
        toModify.timings.firstChunkTime = new Date();
      }
    }
  );

  set(trackNoteDisplayedAction, handle);
}

function handleStreamCloseForCandidate(
  candidate: SideBySideConsultNoteCandidateWithStream,
  handle: GenerationHandle,
  get: Getter,
  set: Setter
) {
  if (candidate.abortController.signal.aborted || !isCurrentGeneration(handle)) {
    return;
  }
  const { sessionId } = handle;
  const candidateInStore = get(noteCandidatesAtomFamily(sessionId)).find(
    (c: SideBySideConsultNoteCandidateWithStream) =>
      c.consult_note_id === candidate.consult_note_id
  );

  // Early returns for invalid states
  if (!candidateInStore || candidateInStore.status === SideBySideStreamStatus.ERROR) {
    return;
  }

  const streamContent = candidateInStore.stream
    .map((s: StreamType) => s.data)
    .join('')
    .trim();
  if (streamContent === '') {
    logger.error('Closed stream with nothing in stream buffer');
    set(updateCandidateStatusAction, {
      ...handle,
      candidateId: candidate.consult_note_id,
      status: SideBySideStreamStatus.ERROR,
    });
    return;
  }

  // Track note creation
  const rawExperimentName = get(rawExperimentNameGenerationModeAtomFamily(sessionId));
  const { timeToFirstToken, timeToLastToken } = calculateTimingMetrics(
    candidateInStore.timings
  );

  const templateId = getConsultNoteSettingsFromQueryCache(sessionId).template_id ?? '';

  track({
    event_name: TrackingEventName.NoteCreated,
    props: {
      ...createExperimentProps(rawExperimentName),
      note_id: candidate.consult_note_id,
      session_id: sessionId,
      type: candidate.is_default_consult_note ? 'default' : 'alternate',
      template_id: templateId,
      time_to_first_token: timeToFirstToken,
      time_to_last_token: timeToLastToken,
      section_count: countMarkdownSections(streamContent),
      is_sectional: getIsSectionalTemplateFromCache(templateId),
      // Device-file→note tracing for Chronicle-origin sessions;
      // resolves to undefined (absent) for mic/upload sessions.
      device_file_name: getChronicleDeviceFileNameForSession(sessionId),
    },
    options: { amplitude: false, posthog: true },
  });

  if (!hasTrackedFirstDocument && candidate.is_default_consult_note) {
    hasTrackedFirstDocument = true;
    track({
      event_name: TrackingEventName.FirstDocumentCreated,
      props: {
        template_type: getTemplateNameFromCache(templateId),
        was_favorited: getIsPinnedTemplateFromCache(templateId),
      },
    });
  }

  // Update candidate status to DONE
  set(updateCandidateStatusAction, {
    ...handle,
    candidateId: candidate.consult_note_id,
    status: SideBySideStreamStatus.DONE,
  });

  // Update end time
  set(
    internalNoteCandidatesAtomFamily(sessionId),
    (draft: SideBySideConsultNoteCandidateWithStream[]) => {
      const toModify = draft.find(
        (candidateInDraft) =>
          candidateInDraft.consult_note_id === candidate.consult_note_id
      );
      if (!toModify) return;
      toModify.timings.endTime = new Date();
    }
  );

  // Handle session invalidation or fallback update
  if (!get(shouldFallbackToSingleNoteViewAtomFamily(sessionId))) {
    invalidateSessionById(sessionId);
    return;
  }

  // Fallback to single note view - update consult note
  updateConsultNoteStreamStatus(sessionId, StreamingStatus.FINISHING_STREAMING);

  const editedNote = getStreamedNoteText(
    get(internalNoteCandidatesAtomFamily(sessionId)),
    candidate.consult_note_id
  );

  updateConsultNote({
    request: {
      edited_note: editedNote,
      content_type: ContentType.MARKDOWN,
      ...getConsultNoteSettingsFromQueryCache(sessionId),
    },
    sessionId,
  })
    .then(() => {
      if (!isCurrentGeneration(handle)) return;
      invalidateSessionById(sessionId);
      const state = get(sideBySideSessionStateAtomFamily(sessionId));
      if (state.phase === 'completingDefault') {
        transitionSessionPhase(get, set, handle, 'COMPLETE');
        set(resetSideBySideAction, handle);
      }
    })
    .catch(logger.error);
}

//
// Actions
//
const updateCandidateStatusAction = atom(
  null,
  (
    _get,
    set,
    input: GenerationHandle & {
      candidateId: string;
      status: SideBySideStreamStatus;
    }
  ) => {
    if (!isCurrentGeneration(input)) return;
    set(internalNoteCandidatesAtomFamily(input.sessionId), (draft) => {
      const toUpdate = draft.find(
        (candidate) => candidate.consult_note_id === input.candidateId
      );
      if (!toUpdate) return;
      toUpdate.status = input.status;
    });
  }
);

const pushCandidateStreamDataAction = atom(
  null,
  (
    get,
    set,
    input: GenerationHandle & { candidateId: string; streamData: StreamType }
  ) => {
    if (!isCurrentGeneration(input)) return;
    const { sessionId, candidateId, streamData } = input;
    set(internalNoteCandidatesAtomFamily(sessionId), (draft) => {
      const toUpdate = draft.find(
        (candidate) => candidate.consult_note_id === candidateId
      );
      if (!toUpdate) return;
      toUpdate.stream.push(streamData);
    });

    if (!get(shouldFallbackToSingleNoteViewAtomFamily(sessionId))) return;
    const noteText = get(internalNoteCandidatesAtomFamily(sessionId))
      .find((candidate) => candidate.consult_note_id === candidateId)
      ?.stream.map((stream) => stream.data)
      .join('');

    if (!noteText) return;
    setConsultNoteQueryDataFallbackFromSideBySide(noteText, sessionId);
  }
);

const trackNoteDisplayedAction = atom(null, (get, set, handle: GenerationHandle) => {
  if (!isCurrentGeneration(handle)) return;
  const { sessionId } = handle;
  if (get(noteDisplayedAnalyticsSentAtomFamily(sessionId))) return;
  if (!get(shouldShowSideBySideNotesAtomFamily(sessionId))) return;

  set(noteDisplayedAnalyticsSentAtomFamily(sessionId), true);
  set(noteDisplayedTimeAtomFamily(sessionId), new Date());

  track({
    event_name: TrackingEventName.NoteDisplayed,
    props: {
      ...createExperimentProps(get(rawExperimentNameGenerationModeAtomFamily(sessionId))),
      session_id: sessionId,
      candidate_note_ids: get(internalNoteCandidatesAtomFamily(sessionId)).map(
        (candidate) => candidate.consult_note_id
      ),
    },
    options: { amplitude: false, posthog: true },
  });
});

export const streamNoteCandidatesAction = atom(
  null,
  async (
    get,
    set,
    input: { sessionId: string; timings?: NoteGenerationTimings } & Partial<
      Pick<GenerationHandle, 'generationId' | 'controller'>
    >
  ) => {
    const handle =
      input.generationId !== undefined && input.controller
        ? ({
            sessionId: input.sessionId,
            generationId: input.generationId,
            controller: input.controller,
          } satisfies GenerationHandle)
        : getCurrentSessionGeneration(input.sessionId);
    if (!handle || !isCurrentGeneration(handle)) return;
    const { sessionId, timings: externalTimings } = input;
    const candidatesAtom = internalNoteCandidatesAtomFamily(sessionId);
    const templateId = getConsultNoteSettingsFromQueryCache(sessionId).template_id;
    const isPinnedTemplate = getIsPinnedTemplateFromCache(templateId);
    const isStarterPackTemplate = getIsStarterTemplateFromCache(templateId);
    const timings =
      externalTimings ??
      trackNoteGenerationStart({
        sessionId,
        is_pinned_template: isPinnedTemplate,
        is_starter_pack_template: isStarterPackTemplate,
      });

    const noteCandidatesToStream = get(candidatesAtom).filter(
      (candidate) => candidate.status === SideBySideStreamStatus.WAITING_START
    );

    // Tracked locally: Skip empties the candidate list after a note is DONE.
    let anyDone = false;
    const isDone = (candidateId: string) =>
      get(candidatesAtom).find((c) => c.consult_note_id === candidateId)?.status ===
      SideBySideStreamStatus.DONE;

    const streamPromises = noteCandidatesToStream.map((candidate) => {
      set(candidatesAtom, (draft) => {
        const toModify = draft.find(
          (candidateInDraft) =>
            candidateInDraft.consult_note_id === candidate.consult_note_id
        );
        if (toModify) toModify.timings.startTime = new Date();
      });
      return streamConsultNoteGenerationV2_2(
        {
          sessionId,
          consultNoteId: candidate.consult_note_id,
          signal: candidate.abortController.signal,
          logPrefix: '[SIDE_BY_SIDE_NOTE_GEN]',
        },
        {
          onMessage: (streamData) => {
            handleStreamMessageForCandidate(candidate, streamData, handle, set);
          },
          onClose: () => {
            handleStreamCloseForCandidate(candidate, handle, get, set);
            if (isDone(candidate.consult_note_id)) anyDone = true;
          },
          onError: (error) => {
            if (
              candidate.abortController.signal.aborted ||
              !isCurrentGeneration(handle)
            ) {
              return;
            }
            logger.error('[SIDE_BY_SIDE_NOTE_GEN] Stream error', {
              errorName: error instanceof Error ? error.name : typeof error,
              error: sanitizeError(error),
            });
            set(updateCandidateStatusAction, {
              ...handle,
              candidateId: candidate.consult_note_id,
              status: SideBySideStreamStatus.ERROR,
            });
          },
        }
      ).catch((error: unknown) => {
        if (!candidate.abortController.signal.aborted && !isAbortError(error)) {
          logger.error('[SIDE_BY_SIDE_NOTE_GEN] Stream rejected', {
            errorName: error instanceof Error ? error.name : typeof error,
            error: sanitizeError(error),
          });
        }
      });
    });
    await Promise.allSettled(streamPromises);
    if (!isCurrentGeneration(handle)) return;

    const hasAnySuccess =
      anyDone ||
      get(candidatesAtom).some(
        (candidate) => candidate.status === SideBySideStreamStatus.DONE
      );
    trackNoteGenerationFinished(
      {
        sessionId,
        is_pinned_template: isPinnedTemplate,
        is_starter_pack_template: isStarterPackTemplate,
      },
      timings,
      hasAnySuccess,
      hasAnySuccess ? undefined : createAllCandidatesFailedError()
    );

    const finalCandidates = get(noteCandidatesAtomFamily(sessionId));
    const successfulCandidates = finalCandidates.filter(
      (candidate) => candidate.status === SideBySideStreamStatus.DONE
    );
    const soleSuccessfulCandidate = successfulCandidates[0];
    if (
      finalCandidates.length > 1 &&
      successfulCandidates.length === 1 &&
      finalCandidates.every(
        (candidate) =>
          candidate.status === SideBySideStreamStatus.DONE ||
          candidate.status === SideBySideStreamStatus.ERROR
      ) &&
      soleSuccessfulCandidate
    ) {
      // One candidate failed: immediately promote the sole successful note.
      // Waiting for selection would hide SBS for 30 minutes and could later
      // choose a failed default candidate.
      const state = get(sideBySideSessionStateAtomFamily(sessionId));
      if (state.phase === 'streamingCandidates') {
        transitionSessionPhase(get, set, handle, 'USE_SINGLE_NOTE');
      }
      setConsultNoteFromCandidate(soleSuccessfulCandidate, sessionId);
      set(resetSideBySideAction, handle);
      return;
    }

    if (
      finalCandidates.length > 0 &&
      finalCandidates.every(
        (candidate) => candidate.status === SideBySideStreamStatus.ERROR
      )
    ) {
      transitionSessionPhase(get, set, handle, 'FAIL');
      setConsultNoteAsFailed(sessionId);
      set(noteCandidatesAtomFamily(sessionId), []);
      set(rawExperimentNameGenerationModeAtomFamily(sessionId), '');
      set(noteDisplayedAnalyticsSentAtomFamily(sessionId), false);
      return;
    }

    const state = get(sideBySideSessionStateAtomFamily(sessionId));
    if (state.phase === 'streamingCandidates') {
      transitionSessionPhase(get, set, handle, 'STREAMS_SETTLED');
    }
  }
);

export const sideBySideSetupAction = atom(
  null,
  async (
    get,
    set,
    input:
      | (GenerationHandle & { reqBody: SideBySideConsultNoteSetupRequest })
      | { sessionID: string; reqBody: SideBySideConsultNoteSetupRequest }
  ) => {
    const sessionId = 'sessionId' in input ? input.sessionId : input.sessionID;
    const handle =
      'generationId' in input ? input : getCurrentSessionGeneration(sessionId);
    if (!handle || !isCurrentGeneration(handle)) return;
    const stateBeforeSetup = get(sideBySideSessionStateAtomFamily(sessionId));
    if (stateBeforeSetup.phase === 'waitingForUpload') {
      transitionSessionPhase(get, set, handle, 'UPLOAD_READY');
    }
    set(noteDisplayedAnalyticsSentAtomFamily(sessionId), false);
    set(noteDisplayedTimeAtomFamily(sessionId), null);
    try {
      const response = await postSetupConsultNote(
        sessionId,
        input.reqBody,
        handle.controller.signal
      );
      if (!isCurrentGeneration(handle)) return;

      const shuffledConsultNotes = shuffle(response.data.consult_note_list);
      set(noteCandidatesAtomFamily(sessionId), {
        candidates: shuffledConsultNotes,
        parentSignal: handle.controller.signal,
      });

      if (response.data.consult_note_list.length > 1) {
        track({
          event_name: TrackingEventName.ExperimentStarted,
          props: createExperimentProps(response.data.generation_mode),
          options: { amplitude: false, posthog: true },
        });
        set(
          rawExperimentNameGenerationModeAtomFamily(sessionId),
          response.data.generation_mode
        );
        const state = get(sideBySideSessionStateAtomFamily(sessionId));
        if (state.phase === 'settingUp') {
          transitionSessionPhase(get, set, handle, 'SETUP_MULTIPLE');
        }
      } else {
        const state = get(sideBySideSessionStateAtomFamily(sessionId));
        if (state.phase === 'settingUp') {
          transitionSessionPhase(get, set, handle, 'USE_SINGLE_NOTE');
        }
      }

      logger.info('Side-by-side precheck completed successfully', {
        sessionID: sessionId,
        candidatesCount: response.data.consult_note_list.length,
      });

      return get(noteCandidatesAtomFamily(sessionId));
    } catch (error) {
      if (handle.controller.signal.aborted || isAbortError(error)) throw error;
      logger.error('Error in side-by-side precheck', {
        errorName: error instanceof Error ? error.name : typeof error,
        error: sanitizeError(error),
      });
      if (isCurrentGeneration(handle)) {
        const state = get(sideBySideSessionStateAtomFamily(sessionId));
        if (state.phase === 'settingUp') {
          transitionSessionPhase(get, set, handle, 'USE_SINGLE_NOTE');
        }
      }
    } finally {
      const state = get(sideBySideSessionStateAtomFamily(sessionId));
      if (state.generationId === handle.generationId || state.generationId === null) {
        set(sideBySideSetupLoadingAtomFamily(sessionId), false);
      }
    }
  }
);

type SelectionInput = Pick<GenerationHandle, 'sessionId' | 'generationId'> & {
  candidateId: string;
  selectionState: Partial<WithSelection>;
};

export const setCandidateSelectionStateAction = atom(
  null,
  (get, set, ...args: [SelectionInput] | [string, Partial<WithSelection>]) => {
    const input: SelectionInput | undefined =
      typeof args[0] === 'string'
        ? (() => {
            const handle = getOnlyActiveGenerationForTests();
            return handle
              ? {
                  sessionId: handle.sessionId,
                  generationId: handle.generationId,
                  candidateId: args[0],
                  selectionState: args[1] ?? {},
                }
              : undefined;
          })()
        : args[0];
    if (!input) return;
    const handle = getCurrentSessionGeneration(input.sessionId);
    if (!handle || handle.generationId !== input.generationId) return;
    set(internalNoteCandidatesAtomFamily(input.sessionId), (draft) => {
      const candidate = draft.find(
        (candidateInDraft) => candidateInDraft.consult_note_id === input.candidateId
      );
      if (candidate) Object.assign(candidate, input.selectionState);
    });
    if (
      input.selectionState.confirmed &&
      get(sideBySideSessionStateAtomFamily(input.sessionId)).phase === 'awaitingSelection'
    ) {
      transitionSessionPhase(get, set, input, 'CONFIRM');
    }
  }
);

export const tryBeginCandidateConfirmationAction = atom(
  null,
  (
    get,
    set,
    input: Pick<GenerationHandle, 'sessionId' | 'generationId'> & {
      candidateId: string;
    }
  ): boolean => {
    const handle = getCurrentSessionGeneration(input.sessionId);
    const state = get(sideBySideSessionStateAtomFamily(input.sessionId));
    if (
      !handle ||
      handle.generationId !== input.generationId ||
      state.generationId !== input.generationId ||
      state.phase !== 'awaitingSelection'
    ) {
      return false;
    }

    const candidate = get(internalNoteCandidatesAtomFamily(input.sessionId)).find(
      (item) => item.consult_note_id === input.candidateId
    );
    if (!candidate || candidate.status !== SideBySideStreamStatus.DONE) return false;

    set(internalNoteCandidatesAtomFamily(input.sessionId), (draft) => {
      const candidateToConfirm = draft.find(
        (item) => item.consult_note_id === input.candidateId
      );
      if (candidateToConfirm) candidateToConfirm.confirmed = true;
    });
    transitionSessionPhase(get, set, handle, 'CONFIRM');
    return true;
  }
);

export const confirmCandidateFailedAction = atom(
  null,
  (
    get,
    set,
    input: Pick<GenerationHandle, 'sessionId' | 'generationId'> & {
      candidateId: string;
    }
  ): boolean => {
    const handle = getCurrentSessionGeneration(input.sessionId);
    const state = get(sideBySideSessionStateAtomFamily(input.sessionId));
    const candidate = get(internalNoteCandidatesAtomFamily(input.sessionId)).find(
      (item) => item.consult_note_id === input.candidateId
    );
    if (
      !handle ||
      handle.generationId !== input.generationId ||
      state.generationId !== input.generationId ||
      state.phase !== 'confirming' ||
      !candidate?.confirmed
    ) {
      return false;
    }

    set(internalNoteCandidatesAtomFamily(input.sessionId), (draft) => {
      const candidateToReset = draft.find(
        (item) => item.consult_note_id === input.candidateId
      );
      if (candidateToReset) candidateToReset.confirmed = false;
    });
    transitionSessionPhase(get, set, handle, 'CONFIRM_FAILED');
    return true;
  }
);

type ConfirmedCandidateInput = Pick<GenerationHandle, 'sessionId' | 'generationId'> & {
  candidate: SideBySideConsultNoteCandidateWithStream;
  ignoreTracking?: boolean;
};

export const afterConfirmCandidateAction = atom(
  null,
  (
    get,
    set,
    ...args:
      | [ConfirmedCandidateInput]
      | [SideBySideConsultNoteCandidateWithStream, string, boolean?]
  ) => {
    const input: ConfirmedCandidateInput | undefined =
      args.length === 1
        ? args[0]
        : (() => {
            const sessionId = args[1];
            const handle = getCurrentSessionGeneration(sessionId);
            return handle
              ? {
                  sessionId,
                  generationId: handle.generationId,
                  candidate: args[0],
                  ignoreTracking: args[2],
                }
              : undefined;
          })();
    if (!input) return;
    const handle = getCurrentSessionGeneration(input.sessionId);
    if (!handle || handle.generationId !== input.generationId) return;
    const { candidate, sessionId } = input;
    const currentCandidate = get(noteCandidatesAtomFamily(sessionId)).find(
      (candidateInStore) => candidateInStore.consult_note_id === candidate.consult_note_id
    );
    if (!currentCandidate) return;

    const state = get(sideBySideSessionStateAtomFamily(sessionId));
    if (state.phase === 'awaitingSelection') {
      transitionSessionPhase(get, set, handle, 'CONFIRM');
    }

    setConsultNoteFromCandidate(currentCandidate, sessionId);

    if (!input.ignoreTracking) {
      const rawExperimentName = get(rawExperimentNameGenerationModeAtomFamily(sessionId));
      const allCandidateIds = get(internalNoteCandidatesAtomFamily(sessionId)).map(
        (candidateInStore) => candidateInStore.consult_note_id
      );
      track({
        event_name: TrackingEventName.NoteSelected,
        props: {
          ...createExperimentProps(rawExperimentName),
          note_id: currentCandidate.consult_note_id,
          session_id: sessionId,
          type: currentCandidate.is_default_consult_note ? 'default' : 'alternate',
          template_id: getConsultNoteSettingsFromQueryCache(sessionId).template_id ?? '',
          candidate_note_ids: allCandidateIds,
        },
        options: { amplitude: false, posthog: true },
      });
    }

    transitionSessionPhase(get, set, handle, 'COMPLETE');
    set(resetSideBySideAction, {
      sessionId,
      generationId: input.generationId,
    });
  }
);

/** Returns whether the default note was kept (still streaming or copied across). */
export const skipSideBySideAction = atom(null, (get, set, sessionId: string): boolean => {
  const handle = getCurrentSessionGeneration(sessionId);
  if (!handle) return false;
  const state = get(sideBySideSessionStateAtomFamily(sessionId));
  if (
    state.generationId !== handle.generationId ||
    !['streamingCandidates', 'awaitingSelection'].includes(state.phase)
  ) {
    return false;
  }
  const candidates = get(noteCandidatesAtomFamily(sessionId));
  const defaultCandidate = candidates.find(
    (candidate) => candidate.is_default_consult_note
  );
  const rawExperimentName = get(rawExperimentNameGenerationModeAtomFamily(sessionId));
  const allCandidateIds = get(internalNoteCandidatesAtomFamily(sessionId)).map(
    (candidate) => candidate.consult_note_id
  );
  track({
    event_name: TrackingEventName.NoteSelectionSkipped,
    props: {
      ...createExperimentProps(rawExperimentName),
      session_id: sessionId,
      candidate_note_ids: allCandidateIds,
    },
    options: { amplitude: false, posthog: true },
  });

  candidates.forEach((candidate) => {
    if (candidate.is_default_consult_note) return;
    if (!candidate.abortController.signal.aborted) candidate.abortController.abort();
  });

  if (!defaultCandidate) {
    logger.error('[SKIP_SBS] No default candidate found', {
      sessionId,
      allCandidateIds,
    });
    transitionSessionPhase(get, set, handle, 'FAIL');
    set(resetSideBySideAction, handle);
    return false;
  }

  transitionSessionPhase(get, set, handle, 'COMPLETE_DEFAULT');
  const isDefaultStillStreaming =
    defaultCandidate.status === SideBySideStreamStatus.WAITING_START ||
    defaultCandidate.status === SideBySideStreamStatus.STREAMING;

  if (isDefaultStillStreaming) {
    candidates.forEach((candidate) => {
      if (candidate.is_default_consult_note) return;
      set(updateCandidateStatusAction, {
        ...handle,
        candidateId: candidate.consult_note_id,
        status: SideBySideStreamStatus.ERROR,
      });
    });

    const partialNote = defaultCandidate.stream.map((stream) => stream.data).join('');
    setConsultNoteQueryDataFallbackFromSideBySide(partialNote, sessionId);
    return true;
  }

  setConsultNoteFromCandidate(defaultCandidate, sessionId);
  transitionSessionPhase(get, set, handle, 'COMPLETE');
  set(resetSideBySideAction, handle);
  return true;
});

export const resetSideBySideAction = atom(
  null,
  (get, set, input?: Pick<GenerationHandle, 'sessionId' | 'generationId'>) => {
    const resolvedInput = input ?? getOnlyActiveGenerationForTests();
    if (!resolvedInput) return;
    const stateAtom = sideBySideSessionStateAtomFamily(resolvedInput.sessionId);
    const current = get(stateAtom);
    if (
      current.generationId !== null &&
      current.generationId !== resolvedInput.generationId
    ) {
      return;
    }
    set(noteCandidatesAtomFamily(resolvedInput.sessionId), []);
    set(stateAtom, {
      ...createInitialSideBySideSessionState(),
      freeTrialPromotion: current.freeTrialPromotion,
    });
  }
);
